import { useState, useEffect, memo, useRef } from 'react'
import { debounce, mapValues } from 'lodash'
import { useForm } from 'react-hook-form'
import {
  Checkbox,
  Col,
  Heading,
  Stack,
  Row,
  Spinner
} from '@leroy-merlin-br/backyard-react-ui'
import { useHistory } from 'react-router-dom'
import { useIsLogged } from 'signup/hooks/context/IsLoggedContext.js'
import {
  NumberField,
  TextField,
  PasswordField,
  Popover
} from 'signup/components'
import * as service from 'signup/services'
import * as S from 'signup/pages/SignUp/styled'
import {
  validateError500,
  flattenObject,
  hasAccountFiscalId,
  handleErrors
} from 'signup/utils'

import {
  factoryAddressRules,
  validateNumberByState
} from 'scripts/react-components/utils/account/factoryAddressRules.js'

import * as validator from 'reactUtils/validators/validators'

import { TextFieldMargin, Button } from './styled'

const FormPJ = ({ recaptchaRef }) => {
  const defaultValues = {
    company: {
      socialName: '',
      fantasyName: ''
    },
    stateRegistration: {
      number: ''
    },
    address: {
      street: '',
      city: '',
      district: '',
      number: null,
      complement: '',
      state: '',
      ibgeCode: '',
      postalCode: {
        number: '',
        type: '',
        description: ''
      }
    },
    email: '',
    emailConfirm: '',
    password: '',
    optins: {
      email: false
    }
  }

  const {
    control,
    register,
    errors,
    setError,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset
  } = useForm({
    mode: 'onChange',
    defaultValues: { ...defaultValues, fiscalId: '' }
  })

  const [apiErrors, setApiErrors] = useState([])
  const [isRequest, setIsRequest] = useState(false)
  const [visibleCompany, setVisibleCompany] = useState(false)
  const [companyInfo, setCompanyInfo] = useState({})
  const [address, setAddress] = useState(null)
  const [loadingCnpj, setLoadingCnpj] = useState(false)
  const [loadingCep, setLoadingCep] = useState(false)
  const [cepNotFound, setCepNotFound] = useState(false)
  const [addressRules, setAddressRules] = useState()
  const lastCep = useRef()

  const validationAddress = address || companyInfo.address

  const history = useHistory()

  const { setIsLogged } = useIsLogged()

  useEffect(() => {
    apiErrors.map(({ code, message, messages }) => {
      return setError(code, {
        type: 'manual',
        message: handleErrors(message, messages)
      })
    })
  }, [apiErrors, setError])

  useEffect(() => {
    if (visibleCompany) {
      mapValues(flattenObject(companyInfo), (value, key) => {
        return setValue(key, value)
      })

      handleAddressRules(flattenObject(companyInfo.address), true)
    }
  }, [setValue, visibleCompany, companyInfo])

  useEffect(() => {
    if (address) {
      mapValues(address, (value, key) => {
        return setValue(`address.${key}`, value || '')
      })
      handleAddressRules(address, false)
    }
  }, [address, setValue])

  useEffect(() => {
    const number = addressRules?.number?.value
    const complement = addressRules?.complement?.value

    if (number) {
      setValue('address.number', number)
    }

    if (complement) {
      setValue('address.complement', complement)
    }
  }, [addressRules, setValue])

  const handleAddressRules = (addressData, isDataFromNfeio) => {
    const newAddressRules = factoryAddressRules(addressData)

    if (isDataFromNfeio) {
      newAddressRules.complement.disabled = true
    }

    setAddressRules(newAddressRules)
  }

  const handleCompany = async event => {
    const { value } = event.target
    const fiscalId = value.replace(/[^0-9]/g, '')

    if (!validator.isCnpj(value)) {
      setVisibleCompany(false)
      return
    }

    setLoadingCnpj(true)
    setAddress(null)
    reset({ ...defaultValues, fiscalId })

    service
      .getCompany(fiscalId)
      .then(data => {
        setIsLogged(true)
        setCompanyInfo(data)
        setVisibleCompany(true)
      })
      .catch(({ data, status }) => {
        setIsLogged(false)

        const shouldRedirect = validateError500(status)

        if (shouldRedirect) {
          history.push('/erro-interno')
        }

        setApiErrors(data.errors)
        setVisibleCompany(false)
      })
      .finally(() => {
        setLoadingCnpj(false)
      })
  }

  const handleCepChange = debounce(event => {
    const { value } = event.target
    const cep = value.replace(/[^0-9]/g, '')

    if (cep === lastCep.current) {
      return
    }

    if (cep.length > 7) {
      searchCep(cep)
    } else {
      setAddress(null)
      setCepNotFound(false)
    }

    lastCep.current = cep
  }, 600)

  const toUpperCaseNumber = value => typeof value === 'string' ? value.toUpperCase() : value

  const handleSubmitPJ = async value => {
    const data = {
      fiscalId: value.fiscalId,
      socialName: value.company.socialName,
      fantasyName: value.company.fantasyName,
      stateRegistration: value.stateRegistration.number,
      address: { ...value.address, number: toUpperCaseNumber(value.address.number) },
      email: value.email,
      emailConfirm: value.emailConfirm,
      password: value.password,
      optins: {
        email: value.optins.email
      }
    }

    if (!validationAddress) {
      return setApiErrors([
        { code: 'address.postalCode.number', message: 'CEP não encontrado' }
      ])
    }

    const recaptchaToken = recaptchaRef.current.executeAsync()

    setIsRequest(true)
    service
      .signupCompany(data, await recaptchaToken)
      .then(({ data }) => {
        setIsLogged(true)
        history.push({
          pathname: '/sucesso',
          state: { redirect: data.redirect }
        })
      })
      .catch(({ data, status }) => {
        setIsLogged(false)

        recaptchaRef.current.reset()

        const shouldRedirect = validateError500(status)

        if (shouldRedirect) {
          history.push('/erro-interno')
        }

        setApiErrors(data.errors)
      })
      .finally(() => {
        setIsRequest(false)
      })
  }

  const searchCep = async cep => {
    setLoadingCep(true)
    setAddress(null)

    reset({
      ...getValues(),
      address: {
        ...defaultValues.address,
        postalCode: {
          ...defaultValues.address.postalCode,
          number: cep
        }
      }
    })

    try {
      const data = await service.getCep(cep)

      setAddress({
        street: data.street,
        city: data.city,
        district: data.district,
        state: data.state,
        number: null,
        ibgeCode: data.ibgeCode,
        'postalCode.number': data.cep,
        'postalCode.type': data.type,
        'postalCode.description': data.description
      })

      setCepNotFound(false)
    } catch (error) {
      setCepNotFound(true)
    }

    setLoadingCep(false)
  }

  return (
    <form onSubmit={handleSubmit(handleSubmitPJ)} data-cy="signup-form-pj">
      <Heading size="mega" noMargin>
        1. Dados da empresa
      </Heading>
      <S.WrapperField hasError={Boolean(errors.fiscalId)}>
        <NumberField
          name="fiscalId"
          label="CNPJ"
          rules={{
            required: 'Insira o CNPJ da empresa',
            validate: value => validator.isCnpj(value) || 'CNPJ inválido'
          }}
          control={control}
          isInvalid={Boolean(errors.fiscalId)}
          validationHint={hasAccountFiscalId(errors, 'CNPJ')}
          format="##.###.###/####-##"
          onChange={handleCompany}
        />
        {loadingCnpj && (
          <span>
            <Spinner size="giga" color="gray" />
          </span>
        )}
      </S.WrapperField>
      <Stack spacing="mega">
        {visibleCompany && (
          <Stack spacing="mega">
            <TextField
              name="company.socialName"
              label="Razão social"
              control={control}
              isDisabled
            />
            <TextField
              name="company.fantasyName"
              label="Nome fantasia"
              control={control}
              isDisabled
            />
            <NumberField
              name="stateRegistration.number"
              label="Inscrição estadual"
              control={control}
              isDisabled
            />
            <S.WrapperField>
              <NumberField
                name="address.postalCode.number"
                label="CEP"
                control={control}
                rules={{
                  required: 'Insira seu CEP',
                  validate: value => validator.isCep(value) || 'CEP inválido'
                }}
                onChange={event => handleCepChange(event)}
                isInvalid={
                  cepNotFound || Boolean(errors.address?.postalCode?.number)
                }
                validationHint={
                  cepNotFound
                    ? 'CEP não encontrado'
                    : errors.address?.postalCode?.number?.message
                }
                format="#####-###"
                isDisabled={Boolean(companyInfo.address?.postalCode?.number)}
              />
              {loadingCep && (
                <span>
                  <Spinner size="giga" color="gray" />
                </span>
              )}
            </S.WrapperField>
            {validationAddress && (
              <Stack spacing="mega">
                <Row>
                  <Col size={{ giga: 8 }}>
                    <TextFieldMargin
                      name="address.street"
                      label="Rua/Avenida"
                      rules={{
                        required: addressRules?.street.required && 'Insira sua rua/avenida',
                        maxLength: { value: 120, message: 'Limite de 120 caracteres' }
                      }}
                      isInvalid={Boolean(errors.address?.street)}
                      validationHint={errors.address?.street?.message}
                      control={control}
                      isDisabled={addressRules?.street.disabled}
                    />
                  </Col>

                  <Col size={{ giga: 4 }}>
                    <TextField
                      name="address.number"
                      label="Número"
                      control={control}
                      rules={
                        addressRules?.number.required && {
                          required: 'Insira o número',
                          validate: value =>
                            validateNumberByState(
                              value,
                              getValues('address.state')
                            ) || 'Número inválido'
                        }
                      }
                      isInvalid={Boolean(errors.address?.number)}
                      validationHint={errors.address?.number?.message}
                      isDisabled={addressRules?.number.disabled}
                    />
                  </Col>
                </Row>

                <TextField
                  name="address.complement"
                  label={
                    <S.ComplementLabel size="kilo" as="h6" noMargin>
                      Complemento <span>- opcional</span>
                    </S.ComplementLabel>
                  }
                  control={control}
                  isDisabled={addressRules?.complement?.disabled}
                  rules={{
                    maxLength: { value: 50, message: 'Limite de 50 caracteres' }
                  }}
                  isInvalid={Boolean(errors.address?.complement)}
                  validationHint={errors.address?.complement?.message}
                />

                <TextField
                  name="address.district"
                  label="Bairro"
                  rules={{
                    required: addressRules?.district.required && 'Insira seu bairro',
                    maxLength: { value: 100, message: 'Limite de 100 caracteres' }
                  }}
                  isInvalid={Boolean(errors.address?.district)}
                  validationHint={errors.address?.district?.message}
                  control={control}
                  isDisabled={addressRules?.district.disabled}
                />

                <Row>
                  <Col size={{ giga: 8 }}>
                    <TextFieldMargin
                      name="address.city"
                      label="Cidade"
                      rules={{
                        required: addressRules?.city.required && 'Insira sua cidade',
                        maxLength: { value: 60, message: 'Limite de 60 caracteres' }
                      }}
                      control={control}
                      isDisabled={addressRules?.city.disabled}
                    />
                  </Col>

                  <Col size={{ giga: 4 }}>
                    <TextField
                      name="address.state"
                      label="Estado"
                      rules={{
                        required: addressRules?.state.required && 'Insira seu estado'
                      }}
                      control={control}
                      isDisabled={addressRules?.state.disabled}
                    />
                  </Col>
                </Row>
              </Stack>
            )}
          </Stack>
        )}

        <Heading size="mega" noMargin>
          2. Dados de acesso
        </Heading>
        <TextField
          name="email"
          type="email"
          label={
            <Popover
              list={{
                title: 'Seu e-mail será usado para:',
                items: [
                  'Te identificar',
                  'Acessar sua conta',
                  'Receber sua nota fiscal',
                  'Receber o status do seu pedido'
                ]
              }}
            >
              E-mail para login
            </Popover>
          }
          rules={{
            required: 'Insira seu e-mail',
            validate: value => validator.isEmail(value) || 'E-mail inválido'
          }}
          control={control}
          autoComplete="new-password"
          isInvalid={Boolean(errors.email)}
          validationHint={errors.email?.message}
        />
        {Boolean(watch('email')) && (
          <TextField
            name="emailConfirm"
            type="email"
            label="Confirmar e-mail"
            rules={{
              required: 'Insira seu e-mail',
              validate: value =>
                value === watch('email') || 'Os e-mail precisam ser iguais'
            }}
            onPaste={e => {
              e.preventDefault()

              return false
            }}
            control={control}
            autoComplete="new-password"
            isInvalid={Boolean(errors.emailConfirm)}
            validationHint={errors.emailConfirm?.message}
          />
        )}
        <PasswordField
          name="password"
          label="Senha"
          rules={{
            required: 'Insira sua senha'
          }}
          control={control}
          autoComplete="new-password"
          isInvalid={Boolean(errors.password)}
          validationHint={errors.password?.message}
        />
        <Checkbox
          name="optins.email"
          validationHint="Nós enviamos: ofertas, conteúdo e notificações"
          ref={register}
        >
          Receber comunicações por e-mail
        </Checkbox>
      </Stack>
      <input type="hidden" name="address.ibgeCode" ref={register} />
      <input type="hidden" name="address.postalCode.type" ref={register} />
      <input
        type="hidden"
        name="address.postalCode.description"
        ref={register}
      />
      <Button isStretch type="submit" isLoading={isRequest}>
        Concordo e quero criar conta
      </Button>
    </form>
  )
}

export default memo(FormPJ)
